import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../../services/api';
import { saveApiKey, getApiKey, hasApiKey } from '../../services/apiKeyService';
import './GenerateAllDescriptions.css';

// Componentes estilizados para substituir o React Bootstrap
const Container = ({ children, className }) => (
  <div className={`container ${className || ''}`}>{children}</div>
);

const Row = ({ children, className }) => (
  <div className={`row ${className || ''}`}>{children}</div>
);

const Col = ({ children, className, md }) => (
  <div className={`col-md-${md || '12'} ${className || ''}`}>{children}</div>
);

const Card = ({ children, className }) => (
  <div className={`card ${className || ''}`}>{children}</div>
);

Card.Header = ({ children }) => (
  <div className="card-header">{children}</div>
);

Card.Body = ({ children }) => (
  <div className="card-body">{children}</div>
);

const Form = ({ children, onSubmit }) => (
  <form onSubmit={onSubmit}>{children}</form>
);

Form.Group = ({ children, className }) => (
  <div className={`form-group ${className || ''}`}>{children}</div>
);

Form.Label = ({ children }) => (
  <label className="form-label">{children}</label>
);

Form.Control = ({ type, placeholder, value, onChange, required, min }) => (
  <input
    type={type}
    className="form-control"
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    required={required}
    min={min}
  />
);

Form.Select = ({ value, onChange, children }) => (
  <select className="form-select" value={value} onChange={onChange}>
    {children}
  </select>
);

Form.Check = ({ type, label, checked, onChange }) => (
  <div className="form-check">
    <input
      type={type}
      className="form-check-input"
      checked={checked}
      onChange={onChange}
      id={`check-${label}`}
    />
    <label className="form-check-label" htmlFor={`check-${label}`}>{label}</label>
  </div>
);

Form.Text = ({ children, className }) => (
  <small className={`form-text ${className || ''}`}>{children}</small>
);

const Button = ({ children, variant, type, disabled, onClick, className }) => (
  <button
    type={type || 'button'}
    className={`btn btn-${variant || 'primary'} ${className || ''}`}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);

const Alert = ({ children, variant, dismissible, onClose }) => (
  <div className={`alert alert-${variant || 'info'} ${dismissible ? 'alert-dismissible' : ''}`} role="alert">
    {children}
    {dismissible && (
      <button type="button" className="btn-close" onClick={onClose} aria-label="Fechar"></button>
    )}
  </div>
);

const ProgressBar = ({ now, label, animated }) => (
  <div className="progress">
    <div
      className={`progress-bar ${animated ? 'progress-bar-animated progress-bar-striped' : ''}`}
      role="progressbar"
      style={{ width: `${now}%` }}
      aria-valuenow={now}
      aria-valuemin="0"
      aria-valuemax="100"
    >
      {label}
    </div>
  </div>
);

const Table = ({ children, striped, bordered, hover, responsive, size }) => {
  let className = 'table';
  if (striped) className += ' table-striped';
  if (bordered) className += ' table-bordered';
  if (hover) className += ' table-hover';
  if (size) className += ` table-${size}`;

  const table = <table className={className}>{children}</table>;

  return responsive ? <div className="table-responsive">{table}</div> : table;
};

const GenerateAllDescriptions = () => {
  // Estados
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [apiKey, setApiKey] = useState('');
  const [salvarChave, setSalvarChave] = useState(true);
  const [categoria, setCategoria] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [apenasVazios, setApenasVazios] = useState(true);
  const [loteInicio, setLoteInicio] = useState(0);
  const [loteFim, setLoteFim] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [progresso, setProgresso] = useState(0);
  const [produtosProcessados, setProdutosProcessados] = useState([]);

  // Buscar categorias disponíveis e chave da API ao carregar o componente
  useEffect(() => {
    // Verificar se existe uma chave da API salva
    if (hasApiKey()) {
      setApiKey(getApiKey());
      setMessage({
        text: 'Chave da API carregada do armazenamento local.',
        type: 'info'
      });

      // Limpar a mensagem após 3 segundos
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }

    const fetchCategorias = async () => {
      try {
        const response = await api.get('/categories.php');
        if (response.data && Array.isArray(response.data)) {
          setCategorias(response.data);
        }
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
        setMessage({
          text: 'Erro ao carregar categorias. Por favor, recarregue a página.',
          type: 'danger'
        });
      }
    };

    fetchCategorias();
  }, []);

  // Função para iniciar o processo de geração
  const iniciarGeracao = async (e) => {
    e.preventDefault();

    if (!apiKey) {
      setMessage({ text: 'Por favor, forneça a chave da API OpenAI.', type: 'warning' });
      return;
    }

    // Salvar a chave da API se a opção estiver marcada
    if (salvarChave) {
      const salvou = saveApiKey(apiKey);
      if (salvou) {
        console.log('Chave da API salva com sucesso.');
      } else {
        console.warn('Não foi possível salvar a chave da API.');
      }
    }

    try {
      setLoading(true);
      setMessage({ text: 'Iniciando processo de geração de descrições...', type: 'info' });
      setResultados(null);
      setProgresso(0);

      // Resultados temporários que serão atualizados durante o streaming
      let resultadosTemp = {
        produtos_processados: 0,
        produtos_com_sucesso: 0,
        produtos_com_erro: 0,
        total_produtos: 0,
        resultados: [],
        erros: []
      };

      const payload = {
        api_key: apiKey,
        apenas_vazios: apenasVazios,
        lote_inicio: loteInicio,
        lote_fim: loteFim
      };

      if (categoria) {
        payload.categoria = categoria;
      }

      // Primeiro, vamos fazer uma requisição POST para iniciar o processo
      try {
        // Criar um FormData para enviar os parâmetros
        const formData = new FormData();
        formData.append('api_key', apiKey);
        formData.append('apenas_vazios', apenasVazios ? '1' : '0');
        formData.append('lote_inicio', loteInicio || '0');
        if (loteFim) formData.append('lote_fim', loteFim);
        if (categoria && categoria !== 'TODAS') formData.append('categoria', categoria);

        console.log('Enviando parâmetros:', {
          api_key: '***oculto***',
          apenas_vazios: apenasVazios ? '1' : '0',
          lote_inicio: loteInicio || '0',
          lote_fim: loteFim || 'não definido',
          categoria: categoria || 'não definida'
        });

        // URL para o endpoint de streaming
        const url = `${api.defaults.baseURL}/ai/generate-all-descriptions.php`;

        // Fazer a requisição POST inicial para iniciar o processo
        const initResponse = await fetch(url, {
          method: 'POST',
          body: formData
        });

        if (!initResponse.ok) {
          const errorText = await initResponse.text();
          console.error('Resposta de erro do servidor:', errorText);
          throw new Error(`Erro na resposta do servidor (${initResponse.status}): ${errorText.substring(0, 200)}${errorText.length > 200 ? '...' : ''}`);
        }

        // Verificar o tipo de conteúdo da resposta
        const contentType = initResponse.headers.get('Content-Type');

        if (contentType && contentType.includes('text/event-stream')) {
          // Processar como stream de eventos
          const reader = initResponse.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          // Função para processar eventos SSE
          const processarEvento = (evento) => {
            if (!evento.trim()) return;
            if (evento.trim() === ':') return; // Heartbeat, ignorar

            const linhas = evento.split('\n');
            let tipo = '';
            let dados = null;

            for (const linha of linhas) {
              if (linha.startsWith('event:')) {
                tipo = linha.substring(6).trim();
              } else if (linha.startsWith('data:')) {
                try {
                  const jsonStr = linha.substring(5).trim();
                  if (jsonStr) {
                    dados = JSON.parse(jsonStr);
                  }
                } catch (e) {
                  console.error('Erro ao parsear dados do evento:', e, linha.substring(5));
                }
              }
            }

            // Se não temos tipo mas temos dados, assumir que é um evento message padrão
            if (!tipo && dados) {
              tipo = 'message';
            }

            if (tipo && dados) {
              console.log(`Evento recebido: ${tipo}`, dados);

              switch (tipo) {
                case 'inicio':
                  setMessage({ text: 'Processamento iniciado. Aguarde...', type: 'info' });
                  resultadosTemp.total_produtos = dados.total_produtos;
                  break;

                case 'produto_processado':
                  // Atualizar contadores
                  resultadosTemp.produtos_processados = dados.produtos_processados;
                  resultadosTemp.produtos_com_sucesso = dados.produtos_com_sucesso;

                  // Adicionar produto aos resultados
                  resultadosTemp.resultados.push(dados.produto);

                  // Atualizar progresso
                  setProgresso(dados.progresso);

                  // Atualizar mensagem
                  setMessage({
                    text: `Processando: ${dados.produtos_processados} de ${dados.total_produtos} produtos (${dados.progresso}%)`,
                    type: 'info'
                  });

                  // Atualizar resultados em tempo real
                  setResultados({ ...resultadosTemp });
                  break;

                case 'produto_erro':
                  // Atualizar contadores
                  resultadosTemp.produtos_processados = dados.produtos_processados;
                  resultadosTemp.produtos_com_erro = dados.produtos_com_erro;

                  // Adicionar erro e produto aos resultados
                  resultadosTemp.erros.push({
                    codigo: dados.produto.codigo,
                    nome: dados.produto.nome,
                    erro: dados.produto.erro
                  });
                  resultadosTemp.resultados.push(dados.produto);

                  // Atualizar progresso
                  setProgresso(dados.progresso);

                  // Atualizar resultados em tempo real
                  setResultados({ ...resultadosTemp });
                  break;

                case 'lote_inicio':
                  setMessage({
                    text: `Iniciando lote ${dados.lote_atual} com ${dados.produtos_no_lote} produtos`,
                    type: 'info'
                  });
                  break;

                case 'lote_fim':
                  setMessage({
                    text: `Lote ${dados.lote_atual} concluído. Progresso: ${dados.progresso}%`,
                    type: 'info'
                  });
                  break;

                case 'concluido':
                  // Atualizar resultados finais
                  setResultados(dados);
                  setMessage({
                    text: `Processo concluído! ${dados.produtos_com_sucesso} produtos atualizados com sucesso.`,
                    type: 'success'
                  });
                  setProgresso(100);
                  setLoading(false);
                  break;

                case 'debug':
                  console.log('Debug do servidor:', dados);
                  break;

                case 'error':
                  throw new Error(dados.error || 'Erro desconhecido');
              }
            }
          };

          // Loop de leitura do stream
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log('Stream concluído');
              break;
            }

            // Decodificar e adicionar ao buffer
            buffer += decoder.decode(value, { stream: true });

            // Processar eventos completos no buffer
            const eventos = buffer.split('\n\n');
            buffer = eventos.pop() || ''; // O último pode estar incompleto

            for (const evento of eventos) {
              if (evento.trim()) {
                processarEvento(evento);
              }
            }
          }
        } else {
          // Fallback para processamento normal se o servidor não suportar SSE
          const data = await initResponse.json();
          setResultados(data);
          setMessage({
            text: `Processo concluído! ${data.produtos_com_sucesso || 0} produtos atualizados com sucesso.`,
            type: 'success'
          });
          setProgresso(100);
        }
      } catch (error) {
        console.error('Erro durante o streaming:', error);
        throw error; // Propagar o erro para o bloco catch externo
      }
    } catch (error) {
      console.error('Erro ao gerar descrições:', error);
      setMessage({
        text: `Erro ao gerar descrições: ${error.message || 'Erro desconhecido'}`,
        type: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Geração em Massa de Descrições</h2>

      {message.text && (
        <Alert variant={message.type} dismissible onClose={() => setMessage({ text: '', type: '' })}>
          {message.text}
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Configurações</Card.Header>
            <Card.Body>
              <Form onSubmit={iniciarGeracao}>
                <Form.Group className="mb-3">
                  <Form.Label>Chave da API OpenAI</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="sk-..."
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Sua chave da API OpenAI. {salvarChave ? 'Será salva localmente no seu navegador.' : 'Não será armazenada.'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Salvar chave da API no navegador"
                    checked={salvarChave}
                    onChange={(e) => setSalvarChave(e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Categoria (opcional)</Form.Label>
                  <Form.Select
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                  >
                    <option value="">Todas as categorias</option>
                    {categorias.map((cat, index) => (
                      <option key={index} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Apenas produtos sem descrição"
                    checked={apenasVazios}
                    onChange={(e) => setApenasVazios(e.target.checked)}
                  />
                </Form.Group>

                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Lote inicial</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        value={loteInicio}
                        onChange={(e) => setLoteInicio(parseInt(e.target.value) || 0)}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Lote final (opcional)</Form.Label>
                      <Form.Control
                        type="number"
                        min={loteInicio}
                        value={loteFim === null ? '' : loteFim}
                        onChange={(e) => {
                          const val = e.target.value;
                          setLoteFim(val === '' ? null : (parseInt(val) || 0));
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Processando...' : 'Iniciar Geração de Descrições'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Status do Processamento</Card.Header>
            <Card.Body>
              {loading && (
                <div className="mb-4">
                  <p>Gerando descrições... Por favor, aguarde.</p>
                  <ProgressBar
                    now={progresso}
                    label={`${progresso}%`}
                    animated
                  />
                  {resultados && (
                    <div className="mt-3">
                      <p>Produtos processados: {resultados.produtos_processados || 0} de {resultados.total_produtos || 0}</p>
                      <p>Sucesso: {resultados.produtos_com_sucesso || 0} | Erros: {resultados.produtos_com_erro || 0}</p>

                      {resultados.resultados && resultados.resultados.length > 0 && (
                        <div className="mt-2 small" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          <p className="mb-1"><strong>Últimos produtos processados:</strong></p>
                          <ul className="list-unstyled">
                            {resultados.resultados.slice(-5).map((produto, idx) => (
                              <li key={idx} className={`text-${produto.status === 'sucesso' ? 'success' : 'danger'}`}>
                                {produto.codigo} - {produto.nome} ({produto.status})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {resultados && (
                <div>
                  <h5>Resumo</h5>
                  <Table striped bordered hover size="sm">
                    <tbody>
                      <tr>
                        <td>Total de produtos:</td>
                        <td>{resultados.total_produtos}</td>
                      </tr>
                      <tr>
                        <td>Produtos processados:</td>
                        <td>{resultados.produtos_processados}</td>
                      </tr>
                      <tr>
                        <td>Produtos atualizados com sucesso:</td>
                        <td className="text-success">{resultados.produtos_com_sucesso}</td>
                      </tr>
                      <tr>
                        <td>Produtos com erro:</td>
                        <td className="text-danger">{resultados.produtos_com_erro}</td>
                      </tr>
                      <tr>
                        <td>Lotes processados:</td>
                        <td>{resultados.lotes_processados} de {resultados.total_lotes}</td>
                      </tr>
                    </tbody>
                  </Table>

                  {resultados.erros && resultados.erros.length > 0 && (
                    <div className="mt-3">
                      <h5>Erros</h5>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>Código</th>
                              <th>Nome</th>
                              <th>Erro</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultados.erros.map((erro, index) => (
                              <tr key={index}>
                                <td>{erro.codigo}</td>
                                <td>{erro.nome}</td>
                                <td>{erro.erro}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!loading && !resultados && (
                <p className="text-muted">
                  Configure os parâmetros e clique em "Iniciar Geração de Descrições" para começar.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {resultados && resultados.resultados && resultados.resultados.length > 0 && (
        <Card className="mb-4">
          <Card.Header>Resultados Detalhados</Card.Header>
          <Card.Body>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Descrição / Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.resultados.map((resultado, index) => (
                    <tr key={index}>
                      <td>{resultado.codigo}</td>
                      <td>{resultado.nome}</td>
                      <td className={resultado.status === 'sucesso' ? 'text-success' : 'text-danger'}>
                        {resultado.status === 'sucesso' ? 'Sucesso' : 'Erro'}
                      </td>
                      <td>
                        {resultado.status === 'sucesso' ? (
                          <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                            {resultado.descricao}
                          </div>
                        ) : (
                          <span className="text-danger">{resultado.erro}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="mb-4">
        <h4>Instruções</h4>
        <ul>
          <li>Este script gera descrições para produtos usando a API do ChatGPT.</li>
          <li>Os produtos são processados em lotes de 10 para evitar sobrecarregar a API.</li>
          <li>Você pode filtrar por categoria ou processar apenas produtos sem descrição.</li>
          <li>Para processar apenas um subconjunto de produtos, especifique os lotes inicial e final.</li>
          <li>O processo pode levar vários minutos dependendo do número de produtos.</li>
          <li>A chave da API OpenAI é necessária e não será armazenada no servidor.</li>
        </ul>
      </div>
    </Container>
  );
};

export default GenerateAllDescriptions;
